import _ from 'lodash';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { ActivityV3Card } from 'src/components/activityv3/ActivityV3Card';
import BottomSheetListSelection from 'src/components/bottomSheet/BottomSheetListSelection';
import { TopNavbar } from 'src/components/common';
import { Icon } from 'src/components/common/icons';
import { Typography } from 'src/components/common/Typography';
import ColorSVGIcon from 'src/components/icon/ColorSVGIcon';
import SVGIcon from 'src/components/icon/SVGIcon';
import { ACTIVITYV3_TYPE_KOR } from 'src/constants/activityv3.enum';
import { ResponseActivityV3ByStudentDto, SubjectType } from 'src/generated/model';

interface ActivityV3ListPageProps {
  data: ResponseActivityV3ByStudentDto[];
}

export function ActivityV3ListPage({ data }: ActivityV3ListPageProps) {
  const { push } = useHistory();
  const [selectedActivity, setSelectedActivity] = useState<SubjectType | null>(null);
  const [selectedSubject, setSelectedSubject] = useState<string | null>(null);
  const activityv3s = data
    ?.filter((activityv3) => selectedActivity === null || activityv3.type === selectedActivity)
    ?.filter((activityv3) => selectedSubject === null || activityv3.subject === selectedSubject);
  const subject = _.uniqBy(activityv3s, 'subject');

  // 바텀시트 관련 상태
  const [isActivityBottomSheetOpen, setIsActivityBottomSheetOpen] = useState(false);
  const [isSubjectBottomSheetOpen, setIsSubjectBottomSheetOpen] = useState(false);
  const [selectedActivityIndex, setSelectedActivityIndex] = useState(0);
  const [selectedSubjectIndex, setSelectedSubjectIndex] = useState(0);

  // 활동 유형 옵션 배열
  const activityOptions = [
    { id: -1, value: null, text: '전체' },
    ...Object.entries(ACTIVITYV3_TYPE_KOR).map(([key, value]) => ({
      id: Number(key),
      value: key as SubjectType,
      text: value,
    })),
  ];

  const subjectOptions = [
    { id: -1, value: null, text: '과목 전체' },
    ...subject.map((subjectItem) => ({
      id: subjectItem.subject,
      value: subjectItem.subject,
      text: subjectItem.subject,
    })),
  ];

  // 데이터가 로드되거나 subject가 변경될 때 기본 선택 상태 유지
  useEffect(() => {
    if (subjectOptions.length > 0) {
      // 현재 선택된 과목이 옵션에 없는 경우 '전체'로 초기화
      const subjectExists = subjectOptions.some((option) => option.value === selectedSubject);
      if (!subjectExists) {
        setSelectedSubject(null);
        setSelectedSubjectIndex(0);
      }
    }
  }, [subject, subjectOptions]);

  // 활동 유형 선택 핸들러
  const handleActivitySelect = (value: SubjectType | null, index: number) => {
    setSelectedActivity(value);
    setSelectedActivityIndex(index);
    setIsActivityBottomSheetOpen(false);
  };

  // 과목 선택 핸들러
  const handleSubjectSelect = (value: string | null, index: number) => {
    setSelectedSubject(value);
    setSelectedSubjectIndex(index);
    setIsSubjectBottomSheetOpen(false);
  };
  return (
    <div>
      <TopNavbar
        title="활동"
        left={
          <ColorSVGIcon.Calendar
            color="gray400"
            size={30}
            onClick={() => {
              push('/student/activity/todo');
            }}
          />
        }
        right={<Icon.Refresh className="h-8 w-8" onClick={() => window.location.reload()} />}
      />
      <main className="flex flex-col gap-4 px-5 py-4">
        <nav className="flex flex-row gap-2">
          {/* 활동 유형 선택 바텀시트 */}
          <BottomSheetListSelection
            isOpen={isActivityBottomSheetOpen}
            onClose={() => setIsActivityBottomSheetOpen(false)}
          >
            {activityOptions.map((item, index) => {
              const isSelected = selectedActivityIndex === index;
              return (
                <div key={index} className="flex flex-row items-center justify-between py-4">
                  <Typography
                    variant={isSelected ? 'title3' : 'body1'}
                    className={isSelected ? 'font-semibold text-primary-orange-800' : 'text-primary-gray-700'}
                    onClick={() => handleActivitySelect(item.value, index)}
                  >
                    {item.text}
                  </Typography>
                  {isSelected && <SVGIcon.Check color="orange800" size={20} />}
                </div>
              );
            })}
          </BottomSheetListSelection>

          {/* 과목 선택 바텀시트 */}
          <BottomSheetListSelection
            isOpen={isSubjectBottomSheetOpen}
            onClose={() => setIsSubjectBottomSheetOpen(false)}
          >
            {subjectOptions.map((subjectItem, index) => {
              const isSelected = selectedSubjectIndex === index;
              return (
                <div key={index} className="flex flex-row items-center justify-between py-4">
                  <Typography
                    variant={isSelected ? 'title3' : 'body1'}
                    className={isSelected ? 'font-semibold text-primary-orange-800' : 'text-primary-gray-700'}
                    onClick={() => handleSubjectSelect(subjectItem.value, index)}
                  >
                    {subjectItem.text}
                  </Typography>
                  {isSelected && <SVGIcon.Check color="orange800" size={20} />}
                </div>
              );
            })}
          </BottomSheetListSelection>

          {/* 활동 유형 선택 버튼 */}
          <div
            className="flex min-w-[88px] cursor-pointer flex-row items-center justify-between rounded-md border border-primary-gray-200 bg-white px-2.5 py-1.5"
            onClick={() => setIsActivityBottomSheetOpen(true)}
          >
            <Typography variant="body3" className="font-medium text-primary-gray-700">
              {selectedActivity !== null ? activityOptions.find((opt) => opt.value === selectedActivity)?.text : '전체'}
            </Typography>
            <SVGIcon.Arrow color="gray700" size={16} rotate={270} />
          </div>

          {/* 과목 선택 버튼 */}
          <div
            className="flex w-full cursor-pointer flex-row items-center justify-between rounded-md border border-primary-gray-200 bg-white px-2.5 py-1.5"
            onClick={() => setIsSubjectBottomSheetOpen(true)}
          >
            <Typography variant="body3" className="font-medium text-primary-gray-700">
              {selectedSubject !== null
                ? subjectOptions.find((opt) => opt.value === selectedSubject)?.text
                : '과목 전체'}
            </Typography>
            <SVGIcon.Arrow color="gray700" size={16} rotate={270} />
          </div>
        </nav>
        <div className="flex flex-col gap-4">
          {activityv3s?.map((activityv3) => <ActivityV3Card key={activityv3.id} activityv3={activityv3} />)}
        </div>
      </main>
    </div>
  );
}
